import { useCallback, useEffect, useState } from "react";
import { useMediaPlaybackStore } from "../../hooks/useMediaPlaybackStore";

interface UseVideoProgressStoreProps {
  currentTime: number;
  signedId: string;
  videoRef: React.RefObject<HTMLMediaElement>;
}

export const useVideoProgressStore = ({
  currentTime,
  signedId,
  videoRef,
}: UseVideoProgressStoreProps) => {
  const getProgress = useMediaPlaybackStore(state => state.getProgress);
  const saveProgress = useMediaPlaybackStore(state => state.saveProgress);
  const deleteProgress = useMediaPlaybackStore(state => state.deleteProgress);
  const storedProgress = getProgress(signedId);

  const [hasSetInitialTime, setHasSetInitialTime] = useState(false);

  useEffect(() => {
    if (!currentTime) return;

    saveProgress(signedId, currentTime);
  }, [currentTime, signedId, saveProgress]);

  // Set initial time only when video starts playing for the first time
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !storedProgress || hasSetInitialTime) return;

    const handleFirstPlay = () => {
      if (!video || !video.duration || hasSetInitialTime) return;

      video.currentTime = storedProgress;
      setHasSetInitialTime(true);
    };

    video.addEventListener("play", handleFirstPlay, { once: true });

    return () => {
      video?.removeEventListener("play", handleFirstPlay);
    };
  }, [videoRef, storedProgress, hasSetInitialTime]);

  const clearStoredProgress = useCallback(
    () => deleteProgress(signedId),
    [deleteProgress, signedId],
  );

  return {
    clearStoredProgress,
  };
};
